import { ENLARGED_HOVER_PREVIEW_Z } from '@src/components/common/topLayerZ';
import { createPortal } from 'react-dom';
import styled, { css } from 'styled-components';

import type { ReactNode } from 'react';

/** Maximum width/height for an enlarged hover preview */
export const ENLARGED_IMAGE_SIZE_PX = 337;
/** Floor so extreme panoramas still produce a card taller than the arrow */
const MIN_ENLARGED_HEIGHT_PX = 48;
/** Minimum top offset to avoid overlapping header/menu area */
const MIN_TOP_OFFSET_PX = 80;
/** Keeps the preview off the viewport edges */
const VIEWPORT_MARGIN_PX = 8;
/** Half the arrow's 24px height — keeps it fully on the card when it slides */
const ARROW_HALF_HEIGHT_PX = 12;

/**
 * Contain-fits the source media inside maxHeight × ENLARGED_IMAGE_SIZE_PX so the
 * card always matches the asset's own aspect ratio.
 */
export const fitPreview = (
  sourceWidth: number,
  sourceHeight: number,
  maxHeight: number,
): { width: number; height: number } | null => {
  if (!sourceWidth || !sourceHeight) return null;
  const aspectRatio = sourceWidth / sourceHeight;
  const height = Math.max(
    Math.min(maxHeight, ENLARGED_IMAGE_SIZE_PX / aspectRatio),
    Math.min(MIN_ENLARGED_HEIGHT_PX, maxHeight),
  );
  return {
    width: Math.min(height * aspectRatio, ENLARGED_IMAGE_SIZE_PX),
    height,
  };
};

/** Same fit for content sized by aspect ratio alone (e.g. a rendered preview). */
export const fitPreviewToAspectRatio = (
  aspectRatio: number,
  maxHeight: number,
) => fitPreview(aspectRatio, 1, maxHeight);

/**
 * The tallest card that still fits between the header zone and the viewport
 * bottom. Callers pass this into `fitPreview` as part of `maxHeight` so a short
 * viewport shrinks the card itself — `fitPreview` derives both dimensions from
 * one height, so width scales with it and the aspect ratio is preserved.
 */
export const viewportMaxPreviewHeight = () =>
  Math.max(
    window.innerHeight - MIN_TOP_OFFSET_PX - VIEWPORT_MARGIN_PX,
    MIN_ENLARGED_HEIGHT_PX,
  );

/**
 * The floating card an enlarged hover preview renders into: anchored beside the
 * thumbnail, vertically centred on it, clamped to the viewport, with an arrow
 * pointing back at the anchor.
 */
export default function EnlargedPreviewCard({
  anchorRect,
  anchorHeight,
  width,
  height,
  direction = 'left',
  darkBackground = false,
  isVisible = true,
  round = false,
  children,
}: {
  anchorRect: DOMRect;
  anchorHeight: number;
  width: number;
  height: number;
  direction?: 'left' | 'right';
  darkBackground?: boolean;
  isVisible?: boolean;
  /** A circle, for a round subject like a headshot (PLAT-12178). */
  round?: boolean;
  children: ReactNode;
}) {
  // Where the card wants to sit: vertically centred on its anchor.
  const desiredTop = anchorRect.top - height / 2 + anchorHeight / 2 - 8;
  // The card is `position: fixed`, so both bounds are the viewport, not the
  // document. The header-zone floor wins on a viewport too short for both.
  const top = Math.min(
    Math.max(desiredTop, MIN_TOP_OFFSET_PX),
    Math.max(
      MIN_TOP_OFFSET_PX,
      window.innerHeight - height - VIEWPORT_MARGIN_PX,
    ),
  );
  // A clamp slid the card off centre, so slide the arrow the other way by the
  // same amount to keep it pointing at the anchor. Computed from the final
  // `top` — clamping in CSS instead would leave the two out of step.
  const arrowMarginPercent = (ARROW_HALF_HEIGHT_PX / height) * 100;
  const arrowPos = Math.min(
    Math.max(50 + ((desiredTop - top) / height) * 100, arrowMarginPercent),
    100 - arrowMarginPercent,
  );

  const rawLeft =
    direction === 'left' ? anchorRect.left - width - 10 : anchorRect.right + 10;
  const left = Math.max(
    VIEWPORT_MARGIN_PX,
    Math.min(rawLeft, window.innerWidth - width - VIEWPORT_MARGIN_PX),
  );

  // Portaled to `document.body`: the card is `position: fixed`, and a host that
  // creates a stacking context — the clip-preview modal wrapper carries a
  // `backdrop-filter` — becomes the containing block for fixed descendants.
  // Rendered inline the card is then clipped by that host's `overflow: hidden`
  // instead of escaping to the viewport, and its z-index is trapped inside the
  // host's layer. The body is the only parent that keeps the clamping above
  // (which measures against the viewport) honest. (PLAT-11200)
  return createPortal(
    <Card
      onClick={e => e.stopPropagation()}
      className="enlarged"
      top={top}
      left={left}
      actualWidth={width}
      previewHeight={height}
      arrowPos={arrowPos}
      arrowDirection={direction}
      isVisible={isVisible}
      darkBackground={darkBackground}
      round={round}
    >
      <Clip>{children}</Clip>
    </Card>,
    document.body,
  );
}

/**
 * Clips the content to the card's radius. The clip can't live on the card
 * itself: the arrow is an `::after` positioned outside its box, so an
 * `overflow: hidden` there would delete it. Needed because a child may bring
 * its own corner radius and an opaque background (CaptionsPreviewThumbnail),
 * which would otherwise paint over the card's rounded corners.
 */
const Clip = styled.div`
  width: 100%;
  height: 100%;
  border-radius: inherit;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Card = styled.div<{
  top: number;
  left: number;
  arrowPos: number;
  arrowDirection: 'left' | 'right';
  isVisible: boolean;
  actualWidth: number;
  previewHeight: number;
  darkBackground?: boolean;
  round?: boolean;
}>`
  position: fixed;
  left: ${props => props.left}px;
  top: ${props => props.top}px;
  width: ${props => props.actualWidth}px;
  height: ${props => props.previewHeight}px;
  border-radius: ${props => (props.round ? '50%' : '12px')};
  background-color: ${props =>
    props.darkBackground ? 'var(--gray-400)' : 'var(--text-secondary)'};
  ${props => props.darkBackground && 'border: 1px solid var(--border);'}
  /* A round preview (a headshot) stands on its own beside its photo: no
     arrow — on a circle it reads as a stray triangle — no card colour behind
     the image, a hairline edge and the menu elevation (PLAT-12178). */
  ${props =>
    props.round &&
    css`
      background-color: var(--background);
      border: 1px solid var(--border);
      box-shadow: var(--elevation-toast);
      &::after {
        display: none;
      }
    `}
  z-index: ${ENLARGED_HOVER_PREVIEW_Z};
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: ${props => (props.isVisible ? 1 : 0)};
  pointer-events: none;
  &::after {
    content: '';
    position: absolute;
    top: ${props => props.arrowPos}%;
    ${props =>
      props.arrowDirection === 'left' &&
      css`
        left: 100%;
        transform: translateY(-50%);
      `}
    ${props =>
      props.arrowDirection === 'right' &&
      css`
        left: -8px;
        transform: translateY(-50%) rotate(180deg);
      `}
    border-style: solid;
    border-width: 12px 0 12px 8px;
    border-color: transparent transparent transparent
      ${props =>
        props.darkBackground ? 'var(--gray-400)' : 'var(--text-secondary)'};
  }
`;
